import { Button } from './Button.js';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Controlled confirmation modal. Reused for every destructive action
 * (sign-out, cancel booking) so they behave consistently (051 FR-020). */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Keep',
  danger,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-black/60"
        onClick={onCancel}
      />
      {/* #950: a positioned modal `div` with the ARIA dialog role (the WAI-ARIA modal pattern). The native `<dialog>`
          is hidden until opened and brings its own positioning and styles, so it is not a drop-in here. */}
      {/* biome-ignore lint/a11y/useSemanticElements: role="dialog" on a positioned div is the intended ARIA modal pattern */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-sm bg-surface-card rounded-xl p-5 shadow-xl"
      >
        <h2 className="text-lg font-bold text-text mb-2">{title}</h2>
        <p className="text-sm text-gray-400 mb-5">{body}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
